// Bộ đọc markdown cho văn bản pháp lý (Chính sách quyền riêng tư, Điều khoản sử dụng) — chỉ phần markdown mà bản
// nháp của pháp chế dùng: "# tiêu đề", "*(hiệu lực)*", "## mục", đoạn văn, danh sách "1." / "1)" / "-", chữ **đậm**,
// *nghiêng*, [link](href) và ký tự thoát "\>". Không import gì — test ở tests/unit/web-legal-doc.test.ts.
// Pháp chế sửa văn bản: thay file trong src/content/legal/, không phải sửa trang.

export interface LegalInline {
  text: string
  bold?: boolean
  italic?: boolean
  href?: string
}

export interface LegalListItem {
  number?: number
  inline: LegalInline[]
  /** Danh sách "1)" ngay sau một mục "1." kết thúc bằng ":" (vd. Hành vi bị cấm). */
  children?: LegalList
}

export interface LegalList {
  type: 'list'
  /** decimal "1." · paren "1)" · bullet "-" */
  style: 'decimal' | 'paren' | 'bullet'
  items: LegalListItem[]
}

export type LegalBlock = { type: 'paragraph'; inline: LegalInline[] } | LegalList

export interface LegalSection {
  id: string
  /** Có khi numberSections — Điều khoản dẫn chiếu "Mục 5.5", "Mục 22". */
  number?: number
  title: string
  blocks: LegalBlock[]
}

export interface LegalDoc {
  title: string
  /** "Áp dụng từ ngày 19/10/2026" — dòng *(…)* ngay dưới tiêu đề. */
  effective: string | null
  intro: LegalBlock[]
  sections: LegalSection[]
}

export function plainText(inline: LegalInline[]): string {
  return inline.map((r) => r.text).join('')
}

/** **đậm**, *nghiêng*, [chữ](link), "\x" = ký tự x. */
export function parseInline(src: string): LegalInline[] {
  const out: LegalInline[] = []
  let buf = ''
  const flush = () => {
    if (buf) out.push({ text: buf })
    buf = ''
  }
  let i = 0
  while (i < src.length) {
    const ch = src[i]!
    if (ch === '\\' && i + 1 < src.length) {
      buf += src[i + 1]
      i += 2
      continue
    }
    if (src.startsWith('**', i)) {
      const end = src.indexOf('**', i + 2)
      if (end > i + 2) {
        flush()
        out.push({ text: unescape(src.slice(i + 2, end)), bold: true })
        i = end + 2
        continue
      }
    }
    if (ch === '*') {
      const end = src.indexOf('*', i + 1)
      if (end > i + 1) {
        flush()
        out.push({ text: unescape(src.slice(i + 1, end)), italic: true })
        i = end + 1
        continue
      }
    }
    if (ch === '[') {
      const m = /^\[([^\]]+)\]\(([^)\s]+)\)/.exec(src.slice(i))
      if (m) {
        flush()
        out.push({ text: unescape(m[1]!), href: m[2]! })
        i += m[0].length
        continue
      }
    }
    buf += ch
    i += 1
  }
  flush()
  return out
}

function unescape(s: string): string {
  return s.replace(/\\(.)/g, '$1')
}

type Token =
  | { kind: 'item'; style: LegalList['style']; number?: number; text: string }
  | { kind: 'text'; text: string }

function tokenize(line: string): Token {
  let m = /^(\d+)\.\s+(.*)$/.exec(line)
  if (m) return { kind: 'item', style: 'decimal', number: Number(m[1]), text: m[2]! }
  m = /^(\d+)\)\s+(.*)$/.exec(line)
  if (m) return { kind: 'item', style: 'paren', number: Number(m[1]), text: m[2]! }
  m = /^-\s+(.*)$/.exec(line)
  if (m) return { kind: 'item', style: 'bullet', text: m[1]! }
  return { kind: 'text', text: line }
}

function buildBlocks(tokens: Token[]): LegalBlock[] {
  const blocks: LegalBlock[] = []
  let i = 0
  const takeList = (style: LegalList['style']): LegalList => {
    const list: LegalList = { type: 'list', style, items: [] }
    while (i < tokens.length) {
      const t = tokens[i]!
      if (t.kind !== 'item' || t.style !== style) break
      const item: LegalListItem = { inline: parseInline(t.text) }
      if (t.number !== undefined) item.number = t.number
      i += 1
      const next = tokens[i]
      if (style === 'decimal' && t.text.trimEnd().endsWith(':') && next?.kind === 'item' && next.style === 'paren') {
        item.children = takeList('paren')
      }
      list.items.push(item)
    }
    return list
  }
  while (i < tokens.length) {
    const t = tokens[i]!
    if (t.kind === 'item') {
      blocks.push(takeList(t.style))
    } else {
      blocks.push({ type: 'paragraph', inline: parseInline(t.text) })
      i += 1
    }
  }
  return blocks
}

export function parseLegalDoc(md: string, opts: { numberSections: boolean; idPrefix: string }): LegalDoc {
  let title = ''
  let effective: string | null = null
  const intro: Token[] = []
  const sections: { title: string; tokens: Token[] }[] = []

  for (const raw of md.split(/\r?\n/)) {
    const line = raw.trim()
    if (!line) continue
    if (line.startsWith('## ')) {
      sections.push({ title: unescape(line.slice(3).trim()), tokens: [] })
      continue
    }
    if (line.startsWith('# ')) {
      title = unescape(line.slice(2).trim())
      continue
    }
    const eff = /^\*\((.+)\)\*$/.exec(line)
    if (eff && sections.length === 0 && effective === null) {
      effective = unescape(eff[1]!.trim())
      continue
    }
    const tok = tokenize(line)
    if (sections.length === 0) intro.push(tok)
    else sections[sections.length - 1]!.tokens.push(tok)
  }

  return {
    title,
    effective,
    intro: buildBlocks(intro),
    sections: sections.map((s, idx) => {
      const sec: LegalSection = { id: `${opts.idPrefix}-${idx + 1}`, title: s.title, blocks: buildBlocks(s.tokens) }
      if (opts.numberSections) sec.number = idx + 1
      return sec
    }),
  }
}
